import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useThemeMode } from "../../ThemeContext";
import { usePermissions } from "../../contexts/PermissionContext";

const toDateKey = (dateOrString) => {
  if (!dateOrString) return null;

  // For string dates, parse YYYY-MM-DD manually to avoid timezone shifts
  if (typeof dateOrString === "string") {
    const isoPart = dateOrString.split("T")[0]; // '2026-02-24T...' -> '2026-02-24'
    const [yStr, mStr, dStr] = isoPart.split("-");
    const year = Number(yStr);
    const month = Number(mStr);
    const day = Number(dStr);
    if (!year || !month || !day) return null;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  // For Date objects, use local calendar date
  const d = dateOrString;
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const TodaysTasksCalendar = ({ todaysTasks, dateField = "start_date" }) => {
  const { mode } = useThemeMode();
  const { hasReadPermission } = usePermissions();
  const navigate = useNavigate();

  const tasks = useMemo(() => todaysTasks || [], [todaysTasks]);
  const [activeDate, setActiveDate] = useState(new Date());

  const tasksByDate = useMemo(() => {
    const map = {};

    const parseToLocalDate = (dateOrString) => {
      if (!dateOrString) return null;

      if (dateOrString instanceof Date && !isNaN(dateOrString.getTime())) {
        return new Date(
          dateOrString.getFullYear(),
          dateOrString.getMonth(),
          dateOrString.getDate(),
        );
      }

      if (typeof dateOrString === "string") {
        const isoPart = dateOrString.split("T")[0];
        const [yStr, mStr, dStr] = isoPart.split("-");
        const year = Number(yStr);
        const month = Number(mStr);
        const day = Number(dStr);
        if (!year || !month || !day) return null;
        return new Date(year, month - 1, day);
      }

      return null;
    };

    tasks.forEach((task) => {
      const start =
        parseToLocalDate(task.start_date || task[dateField]) || null;
      const end =
        parseToLocalDate(task.due_date || task[dateField]) || start;

      if (!start) return;

      const startTime = start.getTime();
      const endTime = end && end.getTime() >= startTime ? end.getTime() : startTime;

      for (let t = startTime; t <= endTime; t += 24 * 60 * 60 * 1000) {
        const currentDate = new Date(t);
        const key = toDateKey(currentDate);
        if (!key) continue;
        if (!map[key]) map[key] = [];
        map[key].push(task); // keeps original order
      }
    });

    return map;
  }, [tasks, dateField]);

  const border = mode === "dark" ? "#333" : "#e0e0e0";
  const text = mode === "dark" ? "#ffffff" : "#333333";
  const muted = mode === "dark" ? "#888888" : "#666666";

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Urgent":
      case "High":
        return "#F44336"; // red
      case "Medium":
        return "#FF9800"; // orange
      case "Low":
        return "#4CAF50"; // green
      default:
        return mode === "dark" ? "#424242" : "#e0e0e0";
    }
  };

  const monthLabel = useMemo(
    () =>
      activeDate.toLocaleString("default", {
        month: "long",
        year: "numeric",
      }),
    [activeDate],
  );

  const goPrevMonth = () => {
    setActiveDate((prev) => {
      const year = prev.getFullYear();
      const month = prev.getMonth();
      return new Date(year, month - 1, 1);
    });
  };

  const goNextMonth = () => {
    setActiveDate((prev) => {
      const year = prev.getFullYear();
      const month = prev.getMonth();
      return new Date(year, month + 1, 1);
    });
  };

  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const key = toDateKey(date);
    const dayTasks = tasksByDate[key] || [];
    if (!dayTasks.length) return null;

    return (
      <div className="task-calendar-tile-tasks">
        {dayTasks.map((task) => {
          const priorityColor = getPriorityColor(task.priority);
          const fullTitle = task.task_title || "Untitled task";
          const displayTitle =
            fullTitle.length > 15 ? `${fullTitle.slice(0, 15)}...` : fullTitle;

          const tooltipLines = [
            fullTitle,
            task.project_name ? `Project: ${task.project_name}` : null,
            `Priority: ${task.priority || "N/A"}`,
          ].filter(Boolean);
          const tooltip = tooltipLines.join("\n");

          return (
            <button
              key={task.id}
              type="button"
              title={tooltip}
              onClick={() => {
                if (!hasReadPermission("task")) return;
                navigate(`/project/${task.project_id}/task/${task.id}`, {
                  state: {
                    fromDashboard: true,
                    projectName: task.project_name,
                  },
                });
              }}
              className="task-calendar-pill"
              style={{
                backgroundColor: priorityColor,
                borderColor: priorityColor,
              }}
            >
              {displayTitle}
            </button>
          );
        })}
      </div>
    );
  };

  const tileClassName = ({ date, view }) => {
    if (view !== "month") return undefined;
    const classes = ["task-calendar-day"];
    const today = new Date();
    if (isSameDay(date, today)) classes.push("task-calendar-day--today");
    const hasTasks = (tasksByDate[toDateKey(date)] || []).length > 0;
    if (hasTasks) classes.push("task-calendar-day--has-tasks");
    return classes.join(" ");
  };

  return (
    <div
      className="task-calendar-card"
      style={{
        backgroundColor: mode === "dark" ? "#0f0f0f" : "#ffffff",
        borderRadius: "12px",
        padding: "24px",
        border: `1px solid ${border}`,
        fontFamily: "Urbanist, Arial, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <h3
          style={{ fontSize: "20px", fontWeight: 600, margin: 0, color: text }}
        >
          Task Calendar
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={goPrevMonth}
            aria-label="Previous month"
            style={{
              width: 28,
              height: 28,
              borderRadius: "999px",
              border: "none",
              backgroundColor: "transparent",
              color: text,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ‹
          </button>
          <span
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: text,
            }}
          >
            {monthLabel}
          </span>
          <button
            type="button"
            onClick={goNextMonth}
            aria-label="Next month"
            style={{
              width: 28,
              height: 28,
              borderRadius: "999px",
              border: "none",
              backgroundColor: "transparent",
              color: text,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ›
          </button>
        </div>
      </div>

      <Calendar
        value={activeDate}
        onChange={setActiveDate}
        view="month"
        tileContent={tileContent}
        tileClassName={tileClassName}
        prev2Label={null}
        next2Label={null}
        showNavigation={false}
        activeStartDate={activeDate}
        className="task-react-calendar"
      />

      <style>
        {`
          .task-react-calendar {
            width: 100%;
            border: none;
            background: transparent;
            font-family: 'Urbanist', Arial, sans-serif;
          }

          .task-react-calendar .react-calendar__navigation {
            margin-bottom: 8px;
          }

          .task-react-calendar .react-calendar__navigation button {
            min-width: 32px;
            background: transparent;
            border: none;
            font-size: 14px;
            font-weight: 600;
            color: ${text};
            border-radius: 8px;
          }

          .task-react-calendar .react-calendar__navigation button:enabled:hover {
            background-color: ${mode === "dark" ? "#151515" : "#f1f5f9"};
          }

          .task-react-calendar .react-calendar__month-view__weekdays__weekday {
            padding: 6px 0;
            font-size: 11px;
            font-weight: 600;
            color: ${muted};
          }

          /* Make the days grid stretch full width of the calendar */
          .task-react-calendar .react-calendar__month-view__days {
            width: 100%;
          }

          .task-react-calendar .react-calendar__tile {
            min-height: 60px;
            padding: 4px;
            color: ${text};
            background: transparent;
            box-sizing: border-box;
          }

          /* Neighbouring month dates still visible but muted */
          .task-react-calendar .react-calendar__month-view__days__day--neighboringMonth {
            color: ${muted};
          }

          /* Remove hover effect on date cells – keep them flat like reference design */
          .task-react-calendar .react-calendar__tile:enabled:hover,
          .task-react-calendar .react-calendar__tile:enabled:focus {
            background: transparent;
            color: inherit;
          }

          /* Slight highlight for selected day only */
          .task-react-calendar .react-calendar__tile--active {
            background: ${mode === "dark" ? "#111827" : "#e0edff"};
            color: ${text};
          }

          /* Ensure our custom day styles win over base tile styles */
          .task-react-calendar .react-calendar__tile.task-calendar-day {
            border-radius: 8px;
            border: none;
            background-color: transparent;
          }

          .task-calendar-day--today {
            box-shadow: 0 0 0 2px ${mode === "dark" ? "rgba(37, 99, 235, 0.5)" : "rgba(37, 99, 235, 0.25)"};
          }

          .task-calendar-tile-tasks {
            margin-top: 4px;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .task-calendar-pill {
            width: 100%;
            padding: 4px 6px;
            border-radius: 6px;
            border-width: 1px;
            border-style: solid;
            font-size: 11px;
            font-weight: 500;
            color: #ffffff;
            text-align: left;
            cursor: pointer;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .task-calendar-pill:disabled {
            cursor: default;
          }

          /* Compact layout on smaller screens */
          @media (max-width: 768px) {
            .task-react-calendar .react-calendar__tile {
              min-height: 48px;
              padding: 2px;
              font-size: 10px;
            }

            .task-react-calendar .react-calendar__month-view__weekdays__weekday {
              font-size: 10px;
            }

            .task-calendar-pill {
              font-size: 10px;
              padding: 3px 4px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default TodaysTasksCalendar;
